// Phase R-4k — Slip Extractor v2 shell (in-AppShell refactor).
//
// History: R8 mounted the eight ported components behind the page's
// OWN secondary sidebar (SlipExtractorSidebar) on a 100dvh flex
// shell. That double-chromed against the global AppShell sidebar
// (empty left column) and the embedded dropzone inherited the
// page-shell height (a giant tall box). R-4k removes the secondary
// sidebar entirely: the page now renders as normal content inside
// AppShell's `.page` (1480px, centered — good on 4K), with its own
// header + a sticky Profile/Tool/History sub-nav.
//
// Section A still uses the Extractor's OWN Profile presentation
// (SlipExtractorProfile) over the SAME useBettingProfile data.
// All section testids preserved verbatim.

import { useEffect, useRef, useState } from "react";

import { useUserTier } from "../../auth/useUserTier.ts";
import { Icon } from "../../components/icons/Icon.tsx";
import { ExtractionHistory } from "../../components/slip-extractor-2/ExtractionHistory.tsx";
import { SE, SE_ROOT_CLASS } from "../../components/slip-extractor-2/seTokens.ts";
import { SlipExtractorForm } from "../../components/slip-extractor-2/SlipExtractorForm.tsx";
import { SlipExtractorProfile } from "../../components/slip-extractor-2/SlipExtractorProfile.tsx";
import { useBettingProfile } from "../../slips/useBettingProfile.ts";

type SectionView = "profile" | "tool" | "history";

const SECTION_EYEBROW: React.CSSProperties = {
  fontFamily: SE.fontMono,
  fontSize: 10,
  letterSpacing: "0.22em",
  textTransform: "uppercase",
  color: SE.textMuted,
  marginBottom: 14,
};
const SECTION_TITLE: React.CSSProperties = {
  fontFamily: SE.fontDisplay,
  fontSize: 24,
  fontWeight: 500,
  color: SE.text,
  margin: "0 0 18px",
  letterSpacing: "-0.01em",
};

export function SlipExtractorPageV2(): JSX.Element {
  const { isPro } = useUserTier();
  // Same intelligence as Slip Autopsy — reuses the user_betting_
  // profile row. N04: empty defaults render meaningful empty states.
  const { profile } = useBettingProfile();

  const [activeView, setActiveView] = useState<SectionView>("profile");

  const sectionARef = useRef<HTMLElement>(null);
  const sectionBRef = useRef<HTMLElement>(null);
  const sectionCRef = useRef<HTMLElement>(null);

  const scrollTo = (
    ref: React.RefObject<HTMLElement | null>,
    view: SectionView,
  ): void => {
    setActiveView(view);
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash.replace("#", "");
    if (hash === "tool") {
      setActiveView("tool");
      sectionBRef.current?.scrollIntoView({ behavior: "auto" });
    } else if (hash === "history") {
      setActiveView("history");
      sectionCRef.current?.scrollIntoView({ behavior: "auto" });
    }
  }, []);

  const tab = (view: SectionView): React.CSSProperties => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    padding: "8px 14px",
    background: activeView === view ? SE.surfaceCard : "transparent",
    color: activeView === view ? SE.text : SE.textSec,
    border: `1px solid ${activeView === view ? SE.borderBright : "transparent"}`,
    borderRadius: 999,
    fontFamily: SE.fontMono,
    fontSize: 10.5,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    cursor: "pointer",
  });

  return (
    <div className={SE_ROOT_CLASS} data-page="slip-extractor-v2">
      {/* ── Page header (replaces the old secondary sidebar brand +
          breadcrumb topbar). Bleeds full-width inside .page. ─────── */}
      <header
        data-testid="sle-topbar"
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 24,
          flexWrap: "wrap",
          paddingBottom: 22,
          marginBottom: 4,
          borderBottom: `1px solid ${SE.border}`,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <nav
            aria-label="Slip Extractor breadcrumb"
            data-testid="sle-breadcrumb"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontFamily: SE.fontMono,
              fontSize: 10,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: SE.textMuted,
              marginBottom: 12,
            }}
          >
            <span>Slip Extractor</span>
            <span aria-hidden style={{ color: SE.textDim }}>
              /
            </span>
            <span style={{ color: SE.textSec }}>Your Profile</span>
          </nav>
          <h1
            className="se-display-xl"
            style={{
              fontFamily: SE.fontDisplay,
              fontSize: 40,
              fontWeight: 500,
              letterSpacing: "-0.02em",
              color: SE.text,
              margin: 0,
              lineHeight: 1.05,
            }}
          >
            Pre-bet intelligence
          </h1>
          <p
            style={{
              margin: "12px 0 0",
              maxWidth: 640,
              fontFamily: SE.fontBody,
              fontSize: 14,
              lineHeight: 1.6,
              color: SE.textSec,
            }}
          >
            Upload an unbet slip and tell STRIKZA what to do with it —
            trim risky legs, build a target-odds subset, or distil the
            highest-EV combination.
          </p>
        </div>

        <button
          type="button"
          onClick={() => scrollTo(sectionBRef, "tool")}
          data-testid="sle-topbar-new"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            fontFamily: SE.fontBody,
            fontSize: 13.5,
            fontWeight: 600,
            padding: "11px 22px",
            background: SE.amber,
            color: SE.surfacePage,
            border: "none",
            borderRadius: 8,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          <Icon name="funnel" size={13} /> Run extractor
        </button>
      </header>

      {/* ── Sticky section sub-nav (replaces the sidebar nav rows).
          Sticks below the global topbar. ──────────────────────────── */}
      <nav
        role="tablist"
        aria-label="Slip Extractor sections"
        data-testid="sle-subnav"
        style={{
          position: "sticky",
          top: "var(--topbar-h, 56px)",
          zIndex: 10,
          display: "flex",
          gap: 8,
          padding: "14px 0",
          margin: "0 0 8px",
          background: SE.surfacePage,
          borderBottom: `1px solid ${SE.border}`,
        }}
      >
        <button
          type="button"
          role="tab"
          aria-selected={activeView === "profile"}
          data-testid="sle-subnav-profile"
          onClick={() => scrollTo(sectionARef, "profile")}
          style={tab("profile")}
        >
          <Icon name="user" size={11} /> Profile
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeView === "tool"}
          data-testid="sle-subnav-tool"
          onClick={() => scrollTo(sectionBRef, "tool")}
          style={tab("tool")}
        >
          <Icon name="funnel" size={11} /> Tool
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeView === "history"}
          data-testid="sle-subnav-history"
          onClick={() => scrollTo(sectionCRef, "history")}
          style={tab("history")}
        >
          <Icon name="clock" size={11} /> History
        </button>
      </nav>

      {/* ── Content (sections A → B → C, brief order N02) ──────────── */}
      <div
        data-testid="sle-main"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 52,
          paddingTop: 24,
        }}
      >
        {/* Section A — the Extractor's OWN Profile presentation */}
        <section data-section="a" data-testid="sle-section-a" ref={sectionARef}>
          <div style={SECTION_EYEBROW}>Intelligence Profile</div>
          <SlipExtractorProfile profile={profile} pro={isPro} />
        </section>

        {/* Section B — Run an Extraction */}
        <section data-section="b" data-testid="sle-section-b" ref={sectionBRef}>
          <div style={SECTION_EYEBROW}>Tool</div>
          <h3 style={SECTION_TITLE}>Run an extraction</h3>
          <SlipExtractorForm />
        </section>

        {/* Section C — Extraction History */}
        <section data-section="c" data-testid="sle-section-c" ref={sectionCRef}>
          <div style={SECTION_EYEBROW}>Record</div>
          <h3 style={SECTION_TITLE}>Extraction history</h3>
          <ExtractionHistory />
        </section>
      </div>
    </div>
  );
}
